import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Minus, Plus, ShoppingBag } from "lucide-react";
import { api, eur } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useOrderedQuantities } from "@/lib/useOrderedQuantities";
import { pressZoom } from "@/lib/usePressZoom";
import ImageZoom from "@/components/ImageZoom";

const TYPE_LABELS = {
  rouge: "Vin rouge",
  blanc: "Vin blanc",
  rose: "Vin rosé",
  champagne: "Champagne",
  coffret: "Coffret",
};

export default function BottleMarquee() {
  const [wines, setWines] = useState([]);
  const [zoomSrc, setZoomSrc] = useState(null);
  const [zoomHover, setZoomHover] = useState(false);
  const navigate = useNavigate();
  const { items, setQty, add } = useCart();
  const orderedMap = useOrderedQuantities();

  useEffect(() => {
    api.get("/wines").then((res) => setWines(res.data)).catch(() => {});
  }, []);

  if (!wines.length) return null;

  const cartQty = (wineId) => items.find((i) => i.wine.id === wineId)?.qty || 0;

  return (
    <section className="pt-[1.5cqw] overflow-hidden" data-testid="bottle-marquee">
      <div className="flex w-max animate-marquee" style={{ animationDuration: "600s" }}>
        {[0, 1].map((half) => (
          <div key={half} className="flex items-stretch gap-[2.4cqw] pr-[2.4cqw]" aria-hidden={half === 1}>
            {wines.map((wine) => {
              const shortName = wine.name.replace(/[«»]/g, "").replace(/\s+—\s+/, " · ").trim();
              const qty = cartQty(wine.id);
              const ordered = orderedMap[wine.id] || 0;
              return (
                <div
                  key={`${half}-${wine.id}`}
                  onClick={() => navigate(`/vins?wine=${wine.id}`)}
                  onKeyDown={(e) => e.key === "Enter" && navigate(`/vins?wine=${wine.id}`)}
                  role="button"
                  tabIndex={half === 1 ? -1 : 0}
                  className="group flex flex-col items-center shrink-0 w-[26cqw] bg-transparent px-[1.2cqw] pt-[1.2cqw] pb-[1cqw] cursor-pointer"
                  data-testid={half === 0 ? `marquee-bottle-${wine.id}` : undefined}
                >
                  <span className="bottle-stage bottle-hover relative flex flex-col items-center justify-end h-[17.5cqw] shrink-0">
                    <img
                      src={wine.image}
                      alt={wine.name}
                      loading="lazy"
                      {...pressZoom(() => {
                        setZoomHover(true);
                        setZoomSrc(wine.image);
                      })}
                      onClick={(e) => {
                        e.stopPropagation();
                        setZoomHover(false);
                        setZoomSrc(wine.image);
                      }}
                      className="h-[17cqw] w-auto object-contain no-frame animate-bottle-turntable bottle-lifted relative z-10 cursor-zoom-in"
                      data-testid={half === 0 ? "marquee-bottle-image" : undefined}
                    />
                  </span>
                  <span
                    className="mt-[1cqw] w-full text-center flex flex-col items-center flex-1"
                    data-testid={half === 0 ? "marquee-bubble" : undefined}
                  >
                    <span className="font-eyebrow text-[1.4cqw] tracking-[0.2em] uppercase font-bold text-[#C5A059] leading-none">
                      {TYPE_LABELS[wine.category] || wine.category}
                    </span>
                    <span className="flex-1 flex items-center justify-center font-display text-[1.9cqw] font-semibold text-[#1C1917] leading-snug mt-[0.4cqw]">
                      {shortName}
                    </span>
                    <span className="block price text-[2.1cqw] text-[#6B1226] mt-[0.4cqw]">
                      {eur(wine.price)}
                    </span>
                    <span
                      className="mt-[0.3cqw] min-h-[2cqw] text-[1.5cqw] font-semibold text-[#8C6D23] leading-tight"
                      data-testid={half === 0 ? "marquee-ordered-qty" : undefined}
                    >
                      {ordered > 0 ? `Déjà commandé : ${ordered} bt` : " "}
                    </span>
                    <span className="mt-[0.8cqw] min-h-[2.9cqw] flex items-center">
                      {qty > 0 ? (
                        <span
                          className="inline-flex items-center gap-[1cqw] bg-[#6B1226]/10 border border-[#6B1226]/25 rounded-full px-[1.2cqw] py-[0.5cqw]"
                          data-testid={half === 0 ? "marquee-qty-badge" : undefined}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setQty(wine.id, qty - 1);
                            }}
                            className="text-[#6B1226] hover:bg-[#6B1226]/10 rounded-full p-[0.3cqw]"
                            data-testid={half === 0 ? "marquee-qty-minus" : undefined}
                            aria-label="Retirer une bouteille"
                          >
                            <Minus size={11} />
                          </button>
                          <span className="text-[1.7cqw] font-bold text-[#6B1226] leading-none whitespace-nowrap">
                            {qty} dans ma commande
                          </span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setQty(wine.id, qty + 1);
                            }}
                            className="text-[#6B1226] hover:bg-[#6B1226]/10 rounded-full p-[0.3cqw]"
                            data-testid={half === 0 ? "marquee-qty-plus" : undefined}
                            aria-label="Ajouter une bouteille"
                          >
                            <Plus size={11} />
                          </button>
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            add(wine, 1);
                          }}
                          className="inline-flex items-center gap-[0.6cqw] text-[1.6cqw] font-semibold text-[#6B1226] border border-[#6B1226]/30 rounded-full px-[1.4cqw] py-[0.5cqw] hover:bg-[#6B1226] hover:text-[#FAF6F0] transition-colors"
                          data-testid={half === 0 ? "marquee-add-button" : undefined}
                        >
                          <ShoppingBag size={10} /> Ajouter
                        </button>
                      )}
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
        ))}
      </div>
      <ImageZoom src={zoomSrc} alt="Bouteille" viaHover={zoomHover} onStick={() => setZoomHover(false)} onClose={() => setZoomSrc(null)} />
    </section>
  );
}
